import { useState } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { LogIn, LayoutDashboard, Menu, X, Facebook, Instagram } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/constants/routes'
import { webUrl } from '@/constants/site'
import { openCookiePreferences } from '@/lib/consent'
import { Wordmark } from '@/components/Wordmark'

// Header/footer replica del sito pubblico (ollylife-web): la fonte è www
// (SiteHeader.tsx, AuthCta.tsx, MobileNav.tsx, SiteFooter.tsx), qui si copia.
// I link marketing/legali puntano a www via webUrl(); "Trova il tuo operatore"
// resta interno (l'app ospita il quiz reale); login/dashboard sono rotte interne.
const NAV_LINKS = [
  { label: 'Trova il tuo operatore', to: ROUTES.MATCHING },
  { label: 'Elenco operatori',       href: webUrl('/cerca') },
  { label: 'News olistiche',         href: webUrl('/blog') },
  { label: 'Manifesto',              href: webUrl('/chi-siamo') },
  { label: 'Per professionisti',     href: webUrl('/diventa-operatore') },
] as const

const SOCIAL_LINKS = [
  { href: 'https://www.instagram.com/ollylifecom/', label: 'Instagram', Icon: Instagram },
  { href: 'https://www.facebook.com/ollylifecom',   label: 'Facebook',  Icon: Facebook },
] as const

const FOOTER_MARKETING = [
  { label: 'Operatori',          href: webUrl('/cerca') },
  { label: 'Discipline',         href: webUrl('/discipline') },
  { label: 'Bisogni',            href: webUrl('/bisogni') },
  { label: 'Trova il tuo match', to: ROUTES.MATCHING },
  { label: 'News olistiche',     href: webUrl('/blog') },
  { label: 'Come funziona',      href: webUrl('/come-funziona') },
  { label: 'Manifesto',          href: webUrl('/chi-siamo') },
  { label: 'Per professionisti', href: webUrl('/diventa-operatore') },
  { label: 'FAQ',                href: webUrl('/faq') },
  { label: 'Contatti',           href: webUrl('/contatti') },
] as const

const FOOTER_LEGAL = [
  { label: 'Privacy Policy',       href: webUrl('/privacy') },
  { label: 'Cookie Policy',        href: webUrl('/cookie-policy') },
  { label: 'Termini di Servizio',  href: webUrl('/termini') },
  { label: 'Disclaimer sanitario', href: webUrl('/disclaimer') },
  { label: 'Note legali',          href: webUrl('/note-legali') },
] as const

export function PublicLayout() {
  const { isAuthenticated, user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)

  const dashboardRoute =
    user?.role === 'ADMIN'    ? ROUTES.ADMIN.DASHBOARD :
    user?.role === 'OPERATOR' ? ROUTES.OPERATOR.DASHBOARD :
    ROUTES.USER.DASHBOARD

  // CTA auth (interna all'app), come AuthCta di www: email (o «Dashboard») se
  // loggato, altrimenti Accedi. min-w-0 + truncate: l'email lunga si tronca
  // invece di spingere fuori il bottone del menu.
  const authCtaCls =
    'flex min-w-0 items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-d'
  const authCta = isAuthenticated ? (
    <Link
      to={dashboardRoute}
      className={authCtaCls}
      title={user?.email || undefined}
      aria-label={user?.email ? `Vai alla dashboard (${user.email})` : 'Vai alla dashboard'}
    >
      <LayoutDashboard className="h-4 w-4 shrink-0" />
      {user?.email ? <span className="max-w-[14rem] truncate">{user.email}</span> : 'Dashboard'}
    </Link>
  ) : (
    <Link to={ROUTES.LOGIN} className={authCtaCls}>
      <LogIn className="h-4 w-4" /> Accedi
    </Link>
  )

  return (
    <div className="flex min-h-screen flex-col bg-bgSoft">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 border-b border-bo bg-bgSoft/95 backdrop-blur-lg">
        <div className="mx-auto flex h-[58px] max-w-7xl items-center gap-7 px-4 sm:px-6">
          <a href={webUrl('/')} aria-label="OLLYlife — home" className="shrink-0">
            <Wordmark />
          </a>

          {/* Nav desktop */}
          <nav className="hidden items-center gap-0.5 md:flex">
            {NAV_LINKS.map((l) =>
              'to' in l ? (
                <Link
                  key={l.label}
                  to={l.to}
                  className="rounded-lg px-3 py-1.5 text-[13px] font-normal text-tx-2 transition-colors hover:bg-brand-l hover:text-tx"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  className="rounded-lg px-3 py-1.5 text-[13px] font-normal text-tx-2 transition-colors hover:bg-brand-l hover:text-tx"
                >
                  {l.label}
                </a>
              ),
            )}
          </nav>

          {/* Azioni a destra */}
          <div className="ml-auto flex min-w-0 items-center gap-2">
            {authCta}

            {/* Toggle mobile */}
            <div className="shrink-0 md:hidden">
              <button
                type="button"
                aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-bo-2 text-tx-2"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Nav mobile */}
        {menuOpen && (
          <>
            <div
              className="fixed inset-0 top-[58px] z-40 bg-tx/30 backdrop-blur-sm md:hidden"
              onClick={() => setMenuOpen(false)}
            />
            <nav className="fixed inset-x-0 top-[58px] z-50 flex flex-col gap-1 border-b border-bo bg-bgSoft p-4 shadow-soft2 md:hidden">
              {NAV_LINKS.map((l) =>
                'to' in l ? (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-tx-2 hover:bg-brand-l hover:text-tx"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.label}
                    href={l.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-tx-2 hover:bg-brand-l hover:text-tx"
                  >
                    {l.label}
                  </a>
                ),
              )}
            </nav>
          </>
        )}
      </header>

      {/* ── Contenuto ── */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ── Footer ── */}
      <footer className="bg-tx py-12 text-white/55">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col items-center gap-5 text-center">
            <div>
              <span className="flex items-end justify-center gap-0.5 font-serif text-2xl leading-none text-white">
                OLLYlife
                <span className="mb-1.5 h-1.5 w-1.5 rounded-full bg-brand-m" />
              </span>
              <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-m">
                Time to reconnect
              </p>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`OLLYlife su ${label}`}
                  title={`OLLYlife su ${label}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-brand-m hover:text-brand-m"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            {/* Navigazione marketing */}
            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-sm">
              {FOOTER_MARKETING.map((l) =>
                'to' in l ? (
                  <Link key={l.label} to={l.to} className="text-white/55 transition-colors hover:text-white">
                    {l.label}
                  </Link>
                ) : (
                  <a key={l.label} href={l.href} className="text-white/55 transition-colors hover:text-white">
                    {l.label}
                  </a>
                ),
              )}
            </nav>

            {/* Navigazione legale */}
            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-white/35">
              {FOOTER_LEGAL.map((l) => (
                <a key={l.label} href={l.href} className="transition-colors hover:text-white">
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={openCookiePreferences}
                className="transition-colors hover:text-white"
              >
                Preferenze cookie
              </button>
            </nav>

            <p className="text-xs text-white/25">
              © {new Date().getFullYear()} OLLYlife - il portale del benessere olistico - Tutti i diritti riservati{' '}
              <a
                href="https://iabaduu.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-white/20 underline-offset-2 transition-colors hover:text-white"
              >
                IABADUU S.r.l.
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}
